// CR229: Client-side points parsing fallback
// Used when normalized data doesn't include points fields

export interface ParsedPointsResult {
  pointsAmount: number | null;
  spendThreshold: number | null;
  pointsProgram: string | null;
}

/**
 * CR229: Extract points amount and spend threshold from headline text
 * This is a client-side fallback when server-side normalization misses values
 */
export function parsePointsFromText(text: string | null | undefined): ParsedPointsResult {
  const result: ParsedPointsResult = {
    pointsAmount: null,
    spendThreshold: null,
    pointsProgram: null,
  };
  
  if (!text) return result;
  
  const normalizedText = text.replace(/\s+/g, ' ').trim();
  
  // Pattern 1: Extract points amount
  const pointsPatterns = [
    /(\d[\d,\s.]*)\s*(pts|points?|bonus\s*points?)/i,
    /(?:get|earn|receive)\s*(\d[\d,\s.]*)\s*(pts|points?)/i,
  ];
  
  for (const pattern of pointsPatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      const cleaned = match[1].replace(/[\s,.]/g, '');
      const num = parseInt(cleaned, 10);
      if (!isNaN(num) && num > 0) {
        result.pointsAmount = num;
        break;
      }
    }
  }
  
  // Pattern 2: Extract spend threshold
  const thresholdPatterns = [
    /(?:for\s+)?every\s*\$?\s*(\d[\d,.]*)/i,
    /per\s*\$?\s*(\d[\d,.]*)\s*(?:spent|spend|purchase)?/i,
    /when\s+you\s+spend\s*\$?\s*(\d[\d,.]*)/i,
    /spend\s*\$?\s*(\d[\d,.]*)/i,
  ];
  
  for (const pattern of thresholdPatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      const cleaned = match[1].replace(/,/g, '');
      const num = parseFloat(cleaned);
      if (!isNaN(num) && num > 0) {
        result.spendThreshold = num;
        break;
      }
    }
  }
  
  // Pattern 3: Extract points program
  const programPatterns = [
    /\b(PC\s*Optimum|Scene\+?|Aeroplan|Air\s*Miles|Petro[-\s]?Points|Triangle|Canadian\s*Tire\s*Money)\b/i,
  ];
  
  for (const pattern of programPatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      result.pointsProgram = match[1].trim();
      break;
    }
  }
  
  return result;
}

/**
 * Format points display value
 * Returns formatted string like "15,000 pts" or "15,000 pts per $100"
 */
export function formatPointsDisplay(
  pointsAmount: number | null,
  spendThreshold: number | null,
  pointsProgram: string | null
): string | null {
  if (!pointsAmount) return null;
  
  const pointsStr = `${pointsAmount.toLocaleString()} pts`;
  
  if (spendThreshold) {
    return `${pointsStr} per $${spendThreshold}`;
  }
  
  if (pointsProgram) {
    return `${pointsStr} ${pointsProgram}`;
  }
  
  return pointsStr;
}

/**
 * Get effective points values with fallback parsing
 * Prefers deal object values, falls back to parsing headline_mechanic
 */
export function getEffectivePointsValues(deal: {
  points_amount?: number | null;
  spend_threshold_amount?: number | null;
  spend_amount?: number | null;
  points_program_name?: string | null;
  headline_mechanic?: string | null;
}): ParsedPointsResult {
  // Try existing values first
  const pointsAmount = deal.points_amount ?? null;
  const spendThreshold = deal.spend_threshold_amount ?? deal.spend_amount ?? null;
  const pointsProgram = deal.points_program_name ?? null;
  
  // If we have points amount, return with what we have
  if (pointsAmount && pointsAmount > 0) {
    return {
      pointsAmount,
      spendThreshold,
      pointsProgram,
    };
  }
  
  // Fallback: try parsing from headline
  if (deal.headline_mechanic) {
    return parsePointsFromText(deal.headline_mechanic);
  }
  
  return {
    pointsAmount: null,
    spendThreshold: null,
    pointsProgram: null,
  };
}
